import { Injectable } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { BaseEntityLayerManager } from '../core/base/base-entity-layer-manager';
import { IReporteBase } from 'modelos/src';

/**
 * ColectivosLayerManager
 *
 * Manager para renderizar colectivos (buses) en el mapa.
 *
 * Características:
 * - Similar a VehiculosLayerManager (sistema de DOS capas)
 * - Background: brújula rotable si tiene orientación, círculo fijo si no
 * - Foreground: icono de bus siempre fijo
 * - Soporta clustering
 * - Color de cluster: naranja (#ff7300)
 *
 * Diferencias con vehículos:
 * - Icono: bus.png (40x40) en lugar de vehiculo.png (18x18)
 * - Color de cluster: naranja en lugar de azul
 */
@Injectable({
  providedIn: 'root',
})
export class ColectivosLayerManager extends BaseEntityLayerManager<
  IReporteBase<'Tracker 4G'>
> {
  // ========== CONFIGURACIÓN ==========
  readonly entityType = 'colectivos';
  readonly supportsClustering = true;
  readonly clusterColor = '#ff7300'; // Naranja

  // IDs de layers específicos
  private backgroundLayerId = 'colectivos-orientation-layer';
  private foregroundLayerId = 'colectivos-layer';
  private unclusteredBackgroundLayerId =
    'colectivos-unclustered-background-layer';
  private unclusteredForegroundLayerId =
    'colectivos-unclustered-foreground-layer';

  // ========== RENDERIZACIÓN ==========

  /**
   * Convierte un reporte de colectivo a un Feature de GeoJSON
   */
  override entityToFeature(
    colectivo: IReporteBase<'Tracker 4G'>,
  ): GeoJSON.Feature<GeoJSON.Point> {
    // OPTIMIZACIÓN: Almacenar en mapa en vez de serializar
    this.storeEntity(colectivo);

    const valores = colectivo.valores;
    const geojson = valores?.geojson;

    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: geojson?.coordinates || [0, 0],
      },
      properties: {
        id: colectivo._id,
        tipo: colectivo.tipoReporte,
        orientation: valores?.orientacion || null,
        hasOrientation:
          valores?.orientacion !== undefined && valores?.orientacion !== null,
        // Metadata adicional para popups
        velocidad: valores?.velocidad,
        // Datos completos para popup
      },
    };
  }

  /**
   * Expresión para el icono foreground (bus)
   */
  override getIconExpression(): string {
    return 'bus-icon';
  }

  /**
   * Expresión para el icono background (brújula si tiene orientación, círculo si no)
   */
  getBackgroundIconExpression(): mapboxgl.Expression {
    return ['case', ['get', 'hasOrientation'], 'compass-icon', 'circle-icon'];
  }

  /**
   * Expresión para la rotación del background
   */
  getIconRotationExpression(): mapboxgl.Expression {
    return ['case', ['get', 'hasOrientation'], ['get', 'orientation'], 0];
  }

  // ========== CREACIÓN DE LAYERS ==========

  /**
   * Crea todos los layers necesarios para colectivos
   */
  protected createLayers(): void {
    if (!this.map) return;

    // 1. LAYERS DE CLUSTERING
    if (this.supportsClustering) {
      this.createStandardClusterLayers();

      // Layers de colectivos NO CLUSTERED (cuando clustering está ON)
      // Sistema de DOS capas para mantener consistencia visual:

      // 1a. Background unclustered (brújula/círculo)
      this.addLayer({
        id: this.unclusteredBackgroundLayerId,
        type: 'symbol',
        source: this.clusteredSourceId,
        filter: ['!', ['has', 'point_count']], // Solo features que NO son clusters
        layout: {
          'icon-image': this.getBackgroundIconExpression(),
          'icon-size': 0.05, // 800 * 0.05 = 40px (compass.svg/circle.png son 800x800)
          'icon-allow-overlap': true,
          'icon-padding': 0,
          'icon-rotation-alignment': 'map', // IMPORTANTE: 'map' para que la brújula apunte al heading correcto
          'icon-rotate': this.getIconRotationExpression(),
          'icon-pitch-alignment': 'viewport', // 'viewport' para que no se pegue al mapa inclinado
          visibility: 'none', // Inicialmente oculto (se muestra cuando clustering ON)
        },
      });

      // 1b. Foreground unclustered (icono bus)
      this.addLayer({
        id: this.unclusteredForegroundLayerId,
        type: 'symbol',
        source: this.clusteredSourceId,
        filter: ['!', ['has', 'point_count']], // Solo features que NO son clusters
        layout: {
          'icon-image': this.getIconExpression(),
          'icon-size': 0.05, // 40px final / 800px natural = 0.05 (bus.png es 800x800)
          'icon-allow-overlap': true,
          'icon-padding': 0,
          'icon-rotation-alignment': 'viewport',
          'icon-pitch-alignment': 'viewport',
          visibility: 'none', // Inicialmente oculto (se muestra cuando clustering ON)
        },
      });
    }

    // 2. BACKGROUND LAYER (brújula rotable o círculo fijo)
    this.addLayer({
      id: this.backgroundLayerId,
      type: 'symbol',
      source: this.sourceId,
      layout: {
        'icon-image': this.getBackgroundIconExpression(),
        'icon-size': 0.05, // 800 * 0.05 = 40px (compass.svg es 800x800)
        'icon-allow-overlap': true,
        'icon-padding': 0,
        'icon-rotation-alignment': 'map', // IMPORTANTE: 'map' para que la brújula apunte al heading correcto
        'icon-rotate': this.getIconRotationExpression(),
        'icon-pitch-alignment': 'viewport', // 'viewport' para que no se pegue al mapa inclinado
      },
    });

    // 3. FOREGROUND LAYER (icono de bus)
    this.addLayer({
      id: this.foregroundLayerId,
      type: 'symbol',
      source: this.sourceId,
      layout: {
        'icon-image': this.getIconExpression(),
        'icon-size': 0.05, // 40px final / 800px natural = 0.05
        'icon-allow-overlap': true,
        'icon-padding': 0,
        'icon-rotation-alignment': 'viewport',
        'icon-pitch-alignment': 'viewport',
      },
    });
  }

  // ========== CLUSTERING ==========

  protected override updateClusteringVisibility(): void {
    if (!this.map || !this.supportsClustering) return;

    if (this.clusteringEnabled) {
      // CLUSTERING ON:
      // - Mostrar clusters (respetando filtro)
      // - Mostrar unclustered (background + foreground, colectivos fuera de clusters, respetando filtro)
      // - Ocultar layers normales (background + foreground)
      // IMPORTANTE: Usar this.isVisible en lugar de true para respetar el estado del filtro
      this.setLayerVisibility(
        `${this.entityType}-cluster-layer`,
        this.isVisible,
      );
      this.setLayerVisibility(
        `${this.entityType}-cluster-count-layer`,
        this.isVisible,
      );
      this.setLayerVisibility(
        this.unclusteredBackgroundLayerId,
        this.isVisible,
      );
      this.setLayerVisibility(
        this.unclusteredForegroundLayerId,
        this.isVisible,
      );
      this.setLayerVisibility(this.backgroundLayerId, false);
      this.setLayerVisibility(this.foregroundLayerId, false);
    } else {
      // CLUSTERING OFF:
      // - Ocultar clusters
      // - Ocultar unclustered (background + foreground)
      // - Mostrar layers normales (background + foreground, respetando filtro)
      this.setLayerVisibility(`${this.entityType}-cluster-layer`, false);
      this.setLayerVisibility(`${this.entityType}-cluster-count-layer`, false);
      this.setLayerVisibility(this.unclusteredBackgroundLayerId, false);
      this.setLayerVisibility(this.unclusteredForegroundLayerId, false);
      // IMPORTANTE: Usar this.isVisible en lugar de true para respetar el estado del filtro
      this.setLayerVisibility(this.backgroundLayerId, this.isVisible);
      this.setLayerVisibility(this.foregroundLayerId, this.isVisible);
    }
  }

  private setLayerVisibility(layerId: string, visible: boolean): void {
    if (!this.map) return;

    if (this.map.getLayer(layerId)) {
      this.map.setLayoutProperty(
        layerId,
        'visibility',
        visible ? 'visible' : 'none',
      );
    }
  }

  // ========== VISIBILIDAD GENERAL ==========

  override setVisibility(visible: boolean): void {
    if (!this.map) return;

    this.isVisible = visible;

    // Si clustering está habilitado, manejar visibility de clusters y unclustered
    if (this.clusteringEnabled && this.supportsClustering) {
      this.setLayerVisibility(`${this.entityType}-cluster-layer`, visible);
      this.setLayerVisibility(
        `${this.entityType}-cluster-count-layer`,
        visible,
      );
      this.setLayerVisibility(this.unclusteredBackgroundLayerId, visible);
      this.setLayerVisibility(this.unclusteredForegroundLayerId, visible);
    } else {
      // Si clustering OFF, manejar visibility de layers normales
      this.setLayerVisibility(this.backgroundLayerId, visible);
      this.setLayerVisibility(this.foregroundLayerId, visible);
    }
  }
}
